import { Component, Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';

import FormRadioButtonGroup from './FormRadioButtonGroup';
import FormTextField from './FormTextField';
import FormSelectField from './FormSelectField';
import FormAutoComplete from './FormAutoComplete';
import FormCheckbox from './FormCheckbox';
import FormRaisedButton from './FormRaisedButton';

const errorStyle = { float: 'left' };

const nonEditableTextFieldProps = {
    floatingLabelFixed: true,
    style: { cursor: 'auto' },
    underlineDisabledStyle: { borderBottom: '1px solid rgb(224, 224, 224)' },
    inputStyle: { color: 'rgba(0, 0, 0, 0.87)' },
};

class Form extends Component {
    static propTypes = {
        intl: intlShape.isRequired,
        labelIdPrefix: PropTypes.string,
        onSubmit: PropTypes.func.isRequired,
        onFormValuesUpdate: PropTypes.func,
        editable: PropTypes.bool,
    };

    static defaultProps = {
        editable: true,
    };

    state = {
        errors: {},
    };

    validate = () => {
        const {
            intl: { formatMessage },
            form,
        } = this.props;

        let errors = {};

        Children.forEach(this.props.children, element => {
            if (
                !element ||
                ![FormTextField, FormAutoComplete, FormSelectField, FormRadioButtonGroup].includes(element.type)
            ) {
                return;
            }

            const {
                props: { name, required },
            } = element;

            if (name && required && !(form[name] || form[name] === 0)) {
                errors[name] = formatMessage({ id: 'RegList.required' });
            }
        });

        this.setState({ errors });

        return !Object.keys(errors).length;
    };

    handleSubmitClick = () => {
        if (!this.validate()) {
            return;
        }

        const { form, children } = this.props;

        let fields = [];

        Children.forEach(children, element => {
            if (
                !element ||
                ![FormTextField, FormAutoComplete, FormSelectField, FormRadioButtonGroup].includes(element.type)
            ) {
                return;
            }

            fields.push(element.props.name);
        });

        const inputData = Object.keys(form)
            .filter(fieldName => fields.includes(fieldName))
            .reduce((acc, curr) => ({ ...acc, [curr]: form[curr] }), {});

        this.props.onSubmit(inputData);
    };

    handleChange = (name, value, numberOnly) => {
        const { errors } = this.state;
        const { form, onFormValuesUpdate, editable } = this.props;

        if (!editable) {
            return;
        }

        if (numberOnly) {
            value = value.replace(/\D/g, '');

            if (value !== '') {
                value = parseInt(value, 10);
            }
        }

        const newForm = {
            ...form,
            [name]: value,
        };

        const newErrors = {
            ...errors,
            [name]: '',
        };

        this.setState({ errors: newErrors });

        onFormValuesUpdate && onFormValuesUpdate(newForm, newErrors);
    };

    enhanceFormField = element => {
        if (
            !element ||
            ![
                FormTextField,
                FormAutoComplete,
                FormSelectField,
                FormCheckbox,
                FormRadioButtonGroup,
                FormRaisedButton,
            ].includes(element.type)
        ) {
            return element;
        }

        const {
            type,
            props: { name, label, floatingLabelText, disabled, action },
        } = element;
        const { errors } = this.state;
        const {
            intl: { formatMessage },
            labelIdPrefix,
            form,
            editable,
        } = this.props;

        let additionalProps = {};
        let generatedLabel;

        if ([FormTextField, FormAutoComplete, FormSelectField, FormCheckbox].includes(type)) {
            generatedLabel = labelIdPrefix ? formatMessage({ id: `${labelIdPrefix}.${name}` }) : null;
        }

        if ([FormTextField, FormAutoComplete, FormSelectField].includes(type)) {
            Object.assign(additionalProps, {
                onChange: this.handleChange,
                floatingLabelText: generatedLabel || floatingLabelText,
                errorStyle,
                errorText: errors[name] || '',
                fullWidth: true,
                disabled: !editable || disabled,
            });
        }

        if (type === FormTextField || type === FormSelectField) {
            additionalProps.value = form[name] == null ? '' : form[name];
        }

        if (type === FormTextField && !editable) {
            Object.assign(additionalProps, nonEditableTextFieldProps);
        }

        if (type === FormAutoComplete) {
            additionalProps.searchText = form[name] == null ? '' : form[name];
        }

        if (type === FormRadioButtonGroup) {
            Object.assign(additionalProps, {
                valueSelected: form[name] == null ? '' : form[name],
                onChange: this.handleChange,
            });
        }

        if (type === FormCheckbox) {
            Object.assign(additionalProps, {
                checked: form[name] == null ? false : form[name],
                onCheck: this.handleChange,
                label: generatedLabel || label,
            });
        }

        if (type === FormRaisedButton && action === 'submit') {
            Object.assign(additionalProps, {
                onClick: this.handleSubmitClick,
            });
        }

        return cloneElement(element, additionalProps);
    };

    render() {
        return Children.map(this.props.children, this.enhanceFormField);
    }
}

export default injectIntl(Form);



// WEBPACK FOOTER //
// ./src/UI/Form.js